import React from 'react'
import ReactDOM from 'react-dom'
import { Mask } from './Mask'
import {
  useKeybind,
  useLockBodyScroll,
  useWindowSize,
} from '../../hooks'
import { getModalTransition } from './modal.helpers'
import { useTransition, animated } from '@react-spring/web'
import { ModalHeader } from './ModalHeader'
import { twMerge } from 'tailwind-merge'
import { faClose } from '@fortawesome/free-solid-svg-icons'
import { Icon } from '../Icon/Icon'

export type ModalType =
  | 'modal'
  | 'fullscreen-modal'
  | 'right-panel'
  | 'left-panel'
  | 'top-panel'
  | 'bottom-panel'
  | 'primary-navbar-more'
  | 'page-modal'

export type ModalProps = {
  isOpen: boolean
  title?: React.ReactNode
  onClose: () => void
  type?: ModalType
  className?: string
  maskClasses?: string
  children?: React.ReactNode
  hideCloseButton?: boolean
  childrenContainerClasses?: string
}

export const Modal = React.memo(
  ({ children, isOpen, title, type = 'left-panel', ...props }: ModalProps) => {
    const [height] = useWindowSize()

    useLockBodyScroll(isOpen)

    // Closed-but-mounted Modals must not hold the LIFO Escape stack
    useKeybind(['Escape'], () => props.onClose(), undefined, !isOpen)

    // Animations
    const animation = useTransition(isOpen ? [isOpen] : [], {
      ...getModalTransition(type),
      config: {
        friction: 35,
        tension: 400,
      },
    })

    return (
      <>
        {animation((styles) =>
          ReactDOM.createPortal(
            <>
              {/* Use the top level isOpen var to let mask control it's own animation */}
              <Mask
                isVisible={isOpen}
                onClick={props.onClose}
                className={twMerge(
                  !isOpen && 'pointer-events-none',
                  props.maskClasses,
                )}
              />
              <animated.div
                role='dialog'
                aria-modal='true'
                style={{
                  ...styles,
                  height:
                    type !== 'modal' &&
                    type !== 'page-modal' &&
                    type !== 'bottom-panel' &&
                    type !== 'fullscreen-modal'
                      ? height
                      : undefined,
                  maxHeight: type !== 'modal' ? undefined : height,
                }}
                className={twMerge(
                  'fixed border isolate border-base-200 shadow-lg bg-white dark:bg-gray-800 z-100 min-w-[min(var(--container-sm),90%)] sm:min-w-[500px] max-w-full overflow-hidden flex flex-col items-stretch justify-center grow max-h-full',
                  getPanelClasses(type),
                  props.className,
                )}>
                {title && <ModalHeader title={title} />}

                <div
                  className={twMerge(
                    'flex flex-col grow overflow-y-auto',
                    props.childrenContainerClasses,
                  )}>
                  {children}
                </div>

                {/* Show the close button for content modals that commonly take the whole screen */}
                {!props.hideCloseButton &&
                  (title ||
                    [
                      'fullscreen-modal',
                      'left-panel',
                      'modal',
                      'right-panel',
                    ].includes(type)) && (
                    <CloseButton
                      className={twMerge(
                        !!title && 'bg-transparent text-base-1000 border-none',
                      )}
                      onClose={props.onClose}
                    />
                  )}
              </animated.div>
            </>,
            document.body,
          ),
        )}
      </>
    )
  },
)

const CloseButton = ({
  className,
  onClose,
}: {
  className?: string
  onClose: () => void
}) => (
  <button
    type='button'
    aria-label='Close'
    className={twMerge(
      // Fixed box; Safari misrenders aspect-ratio on SVG
      'absolute flex z-60 top-2 right-2 size-10 items-center justify-center rounded-full bg-primary-500 border border-primary-700 text-white',
      className,
    )}
    onClick={onClose}>
    <Icon icon={faClose} className='!size-4' />
  </button>
)

const getPanelClasses = (type: ModalType) => {
  if (type === 'left-panel')
    return `top-0 left-0 shadow-2xl drop-shadow-2xl sm:rounded-tr-lg min-h-full h-full sm:rounded-br-lg resize-x`

  if (type === 'right-panel')
    return `top-0 right-0 shadow-2xl drop-shadow-2xl sm:rounded-tl-lg sm:rounded-bl-lg min-h-full h-full max-w-full`

  if (type === 'top-panel')
    return `top-0 right-0 w-full min-h-fit rounded-b-lg drop-shadow-2xl`

  if (type === 'bottom-panel')
    return `bottom-0 right-0 w-full min-h-fit rounded-t-lg drop-shadow-2xl`

  if (type === 'modal')
    return 'top-1/2 left-1/2  w-full max-w-[min(calc(100%-2rem),600px)] rounded-lg max-h-[500px]'

  if (type === 'page-modal')
    return 'top-1/2 left-1/2 w-full rounded-lg h-[600px]'

  if (type === 'fullscreen-modal')
    return 'top-1/2 left-1/2 w-full h-full rounded-lg max-w-[calc(100%-1rem)] max-h-[calc(100%-1rem)] md:max-w-[calc(100%-4rem)] md:max-h-[calc(100%-4rem)] resize-x'
}

export default Modal
